import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { ScreenSize } from "../hooks/useScreenSize";
import useScreenSize from "../hooks/useScreenSize";

// Define the context type
interface SidebarContextType {
    expanded: boolean;
    toggleSidebar: () => void;
    selectedItemId: string;
    setSelectedItemId: (id: string) => void;
    screenType: ScreenSize;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

// Create the context with a default value
const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// Create a provider component
export function SidebarProvider({ children }: { children: ReactNode }) {
    const [expanded, setExpanded] = useState(true);
    const [selectedItemId, setSelectedItemId] = useState("dashboard");
    const [isOpen, setIsOpen] = useState(false);

    // Use the provided hook instead of our custom logic
    const screenType = useScreenSize();

    // Set initial expanded state based on screen size
    useEffect(() => {
        if (screenType === ScreenSize.Mobile) {
            setExpanded(false);
        } else {
            setExpanded(true);
        }
    }, [screenType]);

    const toggleSidebar = () => {
        if (screenType === ScreenSize.Mobile) {
            setIsOpen(!isOpen);
        } else {
            setExpanded(!expanded);
        }
    };

    // Close sidebar when selecting an item on mobile
    useEffect(() => {
        if (screenType === ScreenSize.Mobile) {
            setIsOpen(false);
        }
    }, [selectedItemId, screenType]);

    // Value to be provided to consumers
    const value = {
        expanded,
        toggleSidebar,
        selectedItemId,
        setSelectedItemId,
        screenType,
        isOpen,
        setIsOpen,
    };

    return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

// Custom hook to use the sidebar context
export function useSidebar() {
    const context = useContext(SidebarContext);
    if (context === undefined) {
        throw new Error("useSidebar must be used within a SidebarProvider");
    }
    return context;
}